/**
 * Activity Utilities
 * Pure utility functions for activity manipulation, filtering, and sorting
 * Extracted from legacy activityPlannerService.js during service migration
 */
import { getDisplayLocale } from './dateUtils';

import { parseDurationToMinutes, timeStringToMinutes, minutesToTimeString } from './dateUtils';

/**
 * The real length of a partner product, in minutes, when the catalog knows it.
 *
 * Viator (`durationMinutes`, real elapsed minutes — ≥1440 for multi-day
 * tours), Tiqets (`durationText` "HH:MM" from the cheapest active product),
 * and tourism experiences (`typicalDurationMinutes`) all publish a duration,
 * but the add flow used to drop it and fall back to a flat 60 minutes — so a
 * 12-hour day trip claimed to end an hour after it started. Snake_case
 * spellings are accepted too: these objects reach the modals from several
 * normalizers.
 *
 * Returns 0 when nothing usable is published, meaning "no opinion — keep the
 * caller's default".
 */
export function knownActivityDurationMinutes(item) {
  if (!item) return 0;
  const explicit = item.durationMinutes ?? item.duration_minutes
    ?? item.typicalDurationMinutes ?? item.typical_duration_minutes;
  if (Number.isFinite(explicit) && explicit > 0) return Math.round(explicit);
  // Text shapes ("8h", "2:30", "3 days") — parsed, never shown.
  const text = item.durationText || item.duration_text;
  if (text) {
    const mins = parseDurationToMinutes(text, 0);
    if (mins > 0) return mins;
  }
  return 0;
}

/**
 * Every id a place-like item or trip stop is known under: its Google key
 * (`place_key` on a saved spine row and on a trip stop), its spine place
 * (`attraction_id`), its place id, its saved row's content id and its own id.
 * One place travels under two ids: a Discover sight carries its `atr_` id and,
 * when linked, its Google id; a stop is stored under its `atr_` id when the
 * spine knows the place (spine step 4) while search still returns Google ids,
 * and a stop the planner just added holds the id it was added under until a
 * reload brings the server's. Any shared key is the same place — the two
 * namespaces cannot collide (`atr_` prefix).
 *
 * @returns {string[]} distinct, in that order
 */
export function placeKeysOf(item) {
  if (!item) return [];
  const keys = [item.place_key, item.attraction_id, item.place_id, item.placeId, item.content_id, item.id]
    .filter(k => (typeof k === 'string' && k) || typeof k === 'number')
    .map(String);
  return [...new Set(keys)];
}

/**
 * The days a place is already on, from a map keyed by stop place id
 * (activityDaysMap), under any of the item's ids (placeKeysOf).
 */
export function addedDaysFor(activityDaysMap, item) {
  for (const key of placeKeysOf(item)) {
    const days = activityDaysMap?.[key];
    if (days?.length) return days;
  }
  return [];
}

/**
 * True when an activity is a user-created custom entry ("Checked into hotel",
 * "Picked up rental car") rather than a place/event/tour. Single owner of the
 * rule — surfaces were classifying independently and had already drifted on
 * which type-field spelling they read. Freshly-added customs carry
 * isCustom/source but no type yet (the server stamps activity_type='custom'
 * on save), so every signal is checked.
 */
export function isCustomActivity(activity) {
  if (!activity) return false;
  const type = activity.activityType || activity.activity_type || activity.type;
  return type === 'custom' || activity.isCustom === true || activity.source === 'custom';
}

/**
 * True when the activity's name and place come from its source — a Google
 * place, a landmark, an event, a tour or a partner product — rather than from
 * the person who added it, so the editor shows them read-only (Kurtis,
 * 2026-10-05). Checked on the ids, not only isCustomActivity: a Google-place
 * stop whose 30-day Google row has gone comes back shaped like a custom item
 * (no name), and a renamed one would lose the name again once Google's row
 * returned.
 */
export function isSourceNamedActivity(activity) {
  if (!activity) return false;
  if (activity.placeId || activity.place_id || activity.eventId || activity.event_id) return true;
  return !isCustomActivity(activity);
}

/**
 * Where an edited stop is in its day now, by id — not the index the editor
 * opened at: a reload landing while it's open can shift the day's stops.
 * Returns -1 when the stop has no id or is no longer in the day.
 */
export function findStopIndex(dayActivities, stop) {
  const stopId = (a) => a?.activityId || a?.id;
  const id = stopId(stop);
  if (!id) return -1;
  return (dayActivities || []).findIndex(a => stopId(a) === id);
}

/**
 * Derives the time block (morning/afternoon/evening/all_day) for an activity.
 * Used by the high-level itinerary view to group activities into sections.
 * Falls back to scheduledTime if no explicit timeBlock is set.
 */
export function getTimeBlock(activity) {
  if (activity.timeBlock === 'all_day' || activity.time_block === 'all_day') return 'all_day';
  if (activity.timeBlock) return activity.timeBlock;
  if (activity.time_block) return activity.time_block;
  if (!activity.scheduledTime) return 'morning';
  const hour = parseInt(activity.scheduledTime.split(':')[0], 10);
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/**
 * Display title for an activity across its many stored shapes (place, event,
 * custom entry — camelCase or snake_case). Returns '' when nothing usable is
 * set; callers append their own t() fallback where one is wanted.
 */
export function activityTitle(activity) {
  if (!activity) return '';
  return activity.title || activity.name || activity.customTitle || activity.custom_title || '';
}

/**
 * Time block for a minutes-of-day value, using the canonical boundaries
 * getTimeBlock's time fallback uses (morning < 12:00, afternoon < 17:00,
 * evening onward). Non-finite input falls back to 'morning'.
 */
export function blockOfMinutes(mins) {
  if (!Number.isFinite(mins) || mins < 12 * 60) return 'morning';
  if (mins < 17 * 60) return 'afternoon';
  return 'evening';
}

/** Time block for an "HH:MM" string — same boundaries as blockOfMinutes. */
export function blockOfTimeString(time) {
  const hour = parseInt((time || '').slice(0, 2), 10);
  return Number.isFinite(hour) ? blockOfMinutes(hour * 60) : 'morning';
}

// "Hospitality day" cutoff: a start time before this hour, on an activity the
// user explicitly placed in the EVENING block, means the small hours of the
// NEXT calendar date — the tail of this day's night out (dinner 22:00, club
// 02:00), not this day's early morning. Times at/after the cutoff (an 06:00
// airport run) keep their plain calendar meaning. Only an explicit evening
// placement triggers this — getTimeBlock derives pre-noon starts into
// 'morning', so nothing opts in by accident.
export const LATE_NIGHT_CUTOFF_HOUR = 6;

/** True when an activity is an explicit-evening entry starting in the small
 * hours (00:00–05:59) — i.e. "tonight, after midnight" under the hospitality
 * day convention. */
export function isLateNightEvening(activity) {
  if (!activity?.scheduledTime) return false;
  if (getTimeBlock(activity) !== 'evening') return false;
  const hour = parseInt(activity.scheduledTime.split(':')[0], 10);
  return Number.isFinite(hour) && hour < LATE_NIGHT_CUTOFF_HOUR;
}

/** Minutes-of-day for sorting/live-window purposes, with late-night evening
 * starts shifted past midnight ("02:00" → 1560) so a 2 AM club sorts after
 * the 10 PM dinner and doesn't read as already-started at 6 PM. */
export function effectiveStartMinutes(activity) {
  const [h, m] = activity.scheduledTime.split(':').map(Number);
  const mins = (h || 0) * 60 + (m || 0);
  return isLateNightEvening(activity) ? mins + 24 * 60 : mins;
}

/** Sort / now-line key for a day-card entry: the scheduled "HH:MM" as-is,
 * shifted to a 24h+ string ("02:00" → "26:00") for late-night evening
 * entries so both string compares and timeToMinutes order them after 23:59.
 * Display always comes from the raw scheduledTime, never from this. */
export function activitySortTime(activity) {
  if (!isLateNightEvening(activity)) return activity.scheduledTime;
  const mins = effectiveStartMinutes(activity);
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

// Day-flow order for the blocks. all_day leads (it's the day's anchor — the
// planner renders it as a banner above the timed blocks), then the clock
// blocks in chronological order.
const DAY_BLOCK_ORDER = { all_day: 0, morning: 1, afternoon: 2, evening: 3 };

/**
 * Chronologically order a single day's activities, reconciling the two ways a
 * user can place a stop: an explicit time-of-day block (time_block) and/or an
 * exact scheduled_time. Rule: resolve each item's block via getTimeBlock,
 * order the blocks all_day → morning → afternoon → evening, and WITHIN a
 * block put exact-timed entries first in clock order (late-night evening
 * starts shifted past midnight by activitySortTime), then block-only
 * ("sometime that morning") entries in their original manual order. Stable:
 * equal keys keep input order.
 *
 * Consumers: SharedTrip (rows, pin numbers, route order all derive from this).
 * The high-level planner (HighLevelDayCard) mirrors the same composite rule
 * but assembles it inline — it shares the primitives (getTimeBlock,
 * activitySortTime) yet interleaves transports/continuations per block, so it
 * does NOT call this function; if the ordering rule changes, update both.
 */
export function orderDayActivities(activities) {
  return (activities || [])
    .map((activity, index) => ({ activity, index }))
    .sort((a, b) => {
      const blockDiff = (DAY_BLOCK_ORDER[getTimeBlock(a.activity)] ?? 1)
        - (DAY_BLOCK_ORDER[getTimeBlock(b.activity)] ?? 1);
      if (blockDiff !== 0) return blockDiff;
      const aTimed = !!a.activity.scheduledTime;
      const bTimed = !!b.activity.scheduledTime;
      // Exact-timed entries lead their block, in clock order.
      if (aTimed && bTimed) {
        const timeDiff = activitySortTime(a.activity).localeCompare(activitySortTime(b.activity));
        return timeDiff !== 0 ? timeDiff : a.index - b.index;
      }
      if (aTimed !== bTimed) return aTimed ? -1 : 1;
      // Both block-only: preserve manual (display_order) sequence.
      return a.index - b.index;
    })
    .map(entry => entry.activity);
}

/**
 * Move an activity to a new date, carrying its day-span with it.
 *
 * Day-shifting operations (reorder days, insert a day, delete a day) rewrite
 * every activity's `scheduledDate`. A multi-day activity's `endDate` has to
 * travel by the SAME delta or the span silently misaligns — and if the end
 * date falls outside the trip the server nulls it, quietly collapsing a
 * 3-day tour to one day. Use this instead of spreading `scheduledDate` by
 * hand.
 *
 * Activities with no span (the overwhelming majority) just get the new date.
 */
export function rescheduleActivityToDate(activity, newDate) {
  const moved = { ...activity, scheduledDate: newDate };
  const end = activity.endDate || activity.end_date;
  const oldDate = activity.scheduledDate || activity.scheduled_date;
  if (!end || !oldDate || !newDate) return moved;
  const oldMs = Date.parse(`${oldDate}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  const newMs = Date.parse(`${newDate}T00:00:00Z`);
  if ([oldMs, endMs, newMs].some(Number.isNaN) || endMs <= oldMs) return moved;
  moved.endDate = new Date(newMs + (endMs - oldMs)).toISOString().slice(0, 10);
  return moved;
}

// Block windows in minutes-of-day — same boundaries as getTimeBlock's time
// fallback (morning < 12:00, afternoon < 17:00, evening onward).
const BLOCK_WINDOWS = [
  { key: 'morning', start: 0 },
  { key: 'afternoon', start: 12 * 60 },
  { key: 'evening', start: 17 * 60 },
];

// A spill shorter than this into a later block stays a card-level detail
// ("10:00 - 12:30") rather than spawning a ghost row: the add flow defaults
// duration to 60m, so plenty of entries nick the next block by a few minutes
// without the user ever having chosen a duration at all.
const MIN_SPILL_MINS = 60;

/**
 * Derived "continues into…" entries for the high-level day view: a timed
 * activity whose start + duration runs well into a later block gets a ghost
 * row in each block it occupies, so a 10:00–15:00 tour marks the afternoon
 * as busy instead of leaving it deceptively open. Spill past midnight is
 * capped at 24:00 — activities are single-day by design (things that truly
 * span days are transports/accommodations) — with the wrapped clock time
 * plus plusDays so a row can read "until 00:30 +1" without leaking into the
 * next day's card.
 *
 * Returns { morning: [], afternoon: [...], evening: [...] } of
 * { item, time, untilTime, plusDays }. `time` is the block's start, used as
 * the sort key so continuations lead the block they spill into.
 */
export function getBlockContinuations(activities) {
  const result = { morning: [], afternoon: [], evening: [] };
  (activities || []).forEach(activity => {
    if (!activity?.scheduledTime) return;
    // Day-span activities (endDate set) render as day-level banners on
    // every day they cover — a duration ghost would double-count them.
    if (activity.endDate || activity.end_date) return;
    const blockIdx = BLOCK_WINDOWS.findIndex(b => b.key === getTimeBlock(activity));
    if (blockIdx === -1) return; // all_day renders as a day banner, never spills
    const durationMins = parseDurationToMinutes(activity.duration, 0);
    if (!durationMins) return;
    const endMins = timeStringToMinutes(activity.scheduledTime) + durationMins;
    const cappedEnd = Math.min(endMins, 24 * 60);
    BLOCK_WINDOWS.slice(blockIdx + 1).forEach(block => {
      if (cappedEnd - block.start < MIN_SPILL_MINS) return;
      result[block.key].push({
        item: activity,
        time: minutesToTimeString(block.start),
        untilTime: minutesToTimeString(endMins),
        plusDays: Math.floor(endMins / (24 * 60)),
      });
    });
  });
  return result;
}

/**
 * Filter activities based on weather conditions
 * @param {Array} activities - Array of activities to filter
 * @param {Object} weatherData - Weather data object
 * @returns {Array} - Filtered activities suitable for the weather
 */
export function filterActivitiesByWeather(activities, weatherData) {
  if (!weatherData) return activities;

  return activities.filter(activity => {
    // If it's an indoor activity, weather doesn't matter
    if (activity.category === 'food' ||
        activity.category === 'educational' ||
        activity.category === 'entertainment' ||
        activity.tags?.includes('Indoor')) {
      return true;
    }

    // For outdoor activities, check weather conditions
    if (activity.category === 'outdoor' || activity.category === 'parks' ||
        activity.category === 'nature' ||
        activity.tags?.includes('Outdoor') ||
        activity.tags?.includes('Walking')) {

      const temp = weatherData.temperature;
      const condition = weatherData.condition?.toLowerCase();
      const windSpeed = weatherData.windSpeed || 0;
      const precipitation = weatherData.precipitation || 0;

      // Filter out activities in bad weather
      if (condition?.includes('rain') || condition?.includes('storm') || precipitation > 0.1) {
        return false;
      }

      if (windSpeed > 25) { // High wind
        return false;
      }

      if (temp < 35 || temp > 95) { // Extreme temperatures
        return false;
      }
    }

    return true;
  });
}

/**
 * Filter activities by various criteria
 * @param {Array} activities - Activities to filter
 * @param {Object} filters - Filter criteria object
 * @returns {Array} - Filtered activities
 */
export function filterActivities(activities, filters = {}) {
  let filtered = [...activities];

  // Category filter
  if (filters.category && filters.category !== 'all') {
    filtered = filtered.filter(a => a.category === filters.category);
  }

  // Price range filter
  if (filters.priceRange && filters.priceRange !== 'all') {
    filtered = filtered.filter(a => a.priceRange === filters.priceRange);
  }

  // Duration filter
  if (filters.duration && filters.duration !== 'all') {
    filtered = filtered.filter(a => a.duration === filters.duration);
  }

  // Difficulty filter
  if (filters.difficulty && filters.difficulty !== 'all') {
    filtered = filtered.filter(a => a.difficulty === filters.difficulty);
  }

  // Location filter
  if (filters.location && filters.location.trim()) {
    filtered = filtered.filter(a =>
      a.location?.toLowerCase().includes(filters.location.toLowerCase())
    );
  }

  // Search query
  if (filters.search && filters.search.trim()) {
    const query = filters.search.toLowerCase();
    filtered = filtered.filter(a =>
      a.title?.toLowerCase().includes(query) ||
      a.description?.toLowerCase().includes(query) ||
      a.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }

  return filtered;
}

/**
 * Sort activities by various criteria
 * @param {Array} activities - Activities to sort
 * @param {string} sortBy - Sort criteria
 * @returns {Array} - Sorted activities
 */
export function sortActivities(activities, sortBy = 'newest') {
  const sorted = [...activities];

  switch (sortBy) {
    case 'newest':
      return sorted.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    case 'oldest':
      return sorted.sort((a, b) => new Date(a.savedAt) - new Date(b.savedAt));
    case 'alphabetical':
      // Collate in the active UI language (accent ordering differs by locale).
      return sorted.sort((a, b) => a.title.localeCompare(b.title, getDisplayLocale()));
    case 'price-low':
      const priceOrder = { 'Free': 0, '$': 1, '$$': 2, '$$$': 3 };
      return sorted.sort((a, b) => (priceOrder[a.priceRange] || 999) - (priceOrder[b.priceRange] || 999));
    case 'price-high':
      const priceOrderDesc = { '$$$': 0, '$$': 1, '$': 2, 'Free': 3 };
      return sorted.sort((a, b) => (priceOrderDesc[a.priceRange] || 999) - (priceOrderDesc[b.priceRange] || 999));
    default:
      return sorted;
  }
}

/**
 * Generate social media sharing URLs for trips and activities
 * @param {string} shareUrl - The URL to share
 * @param {string} text - The text description for the share
 * @returns {Object} - Object containing sharing URLs for different platforms
 */
export function generateSocialShareUrls(shareUrl, text) {
  if (!shareUrl || !text) {
    return {};
  }

  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(shareUrl);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    instagram: shareUrl, // Instagram doesn't support direct URL sharing, so just return the URL
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    email: `mailto:?subject=${encodedText}&body=${encodedText}%20${encodedUrl}`
  };
}
